import { useState } from 'react';
import { useI18n } from '@/lib/i18n';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus } from '@phosphor-icons/react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { addDays, nextMonday, nextTuesday, nextWednesday, nextThursday, nextFriday, nextSaturday, nextSunday, parseISO, startOfDay } from 'date-fns';

function parseQuickAddSyntax(raw, tags = []) {
  let title = raw;
  let priority = null;
  let due_datetime = null;
  let tagIds = [];

  // Priority: !1, !2, !3
  title = title.replace(/!([123])\b/g, (_, n) => { priority = parseInt(n); return ''; });

  // Date: @token
  title = title.replace(/@(\S+)/g, (_, token) => {
    const lower = token.toLowerCase();
    const today = startOfDay(new Date());
    if (lower === 'today') { due_datetime = addDays(today, 0).toISOString(); return ''; }
    if (lower === 'tomorrow') { due_datetime = addDays(today, 1).toISOString(); return ''; }
    const dayFns = { monday: nextMonday, tuesday: nextTuesday, wednesday: nextWednesday, thursday: nextThursday, friday: nextFriday, saturday: nextSaturday, sunday: nextSunday };
    if (dayFns[lower]) { due_datetime = dayFns[lower](today).toISOString(); return ''; }
    // ISO date like 2026-05-01
    if (/^\d{4}-\d{2}-\d{2}$/.test(token)) {
      try { due_datetime = parseISO(token + 'T09:00:00').toISOString(); return ''; } catch {}
    }
    return `@${token}`;
  });

  // Tags: #tagname
  title = title.replace(/#(\S+)/g, (_, name) => {
    const found = tags.find(tg => tg.name.toLowerCase() === name.toLowerCase());
    if (found) { tagIds.push(found.id); return ''; }
    return `#${name}`;
  });

  return { title: title.trim().replace(/\s+/g, ' '), priority, due_datetime, tagIds };
}

export default function QuickAdd({ onTaskAdded, listId, tags = [] }) {
  const { t } = useI18n();
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAdd = async () => {
    if (!title.trim()) return;
    setLoading(true);
    try {
      const { title: cleanTitle, priority, due_datetime, tagIds } = parseQuickAddSyntax(title, tags);
      const payload = { title: cleanTitle || title.trim() };
      if (listId) payload.list_id = listId;
      if (priority !== null) payload.priority = priority;
      if (due_datetime) payload.due_datetime = due_datetime;
      if (tagIds.length) payload.tags = tagIds;
      const res = await api.post('/tasks', payload);
      setTitle('');
      onTaskAdded?.(res.data);
    } catch {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex gap-2" data-testid="quick-add">
      <Input
        data-testid="quick-add-input"
        placeholder={t('task.quickAdd')}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        className="flex-1"
      />
      <Button data-testid="quick-add-btn" size="icon" onClick={handleAdd} disabled={loading || !title.trim()}>
        <Plus size={18} weight="bold" />
      </Button>
    </div>
  );
}
